function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == _typeof(i) ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != _typeof(t) || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != _typeof(i)) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { tracingContextFromHeaders, logger, dropUndefinedKeys, addNonEnumerableProperty } from '@sentry/utils';
import { DEBUG_BUILD } from '../debug-build.js';
import { getCurrentScope, withScope } from '../exports.js';
import { getCurrentHub, runWithAsyncContext, getIsolationScope } from '../hub.js';
import { handleCallbackErrors } from '../utils/handleCallbackErrors.js';
import { hasTracingEnabled } from '../utils/hasTracingEnabled.js';
import { spanToJSON, spanTimeInputToSeconds } from '../utils/spanUtils.js';

/**
 * Wraps a function with a transaction/span and finishes the span after the function is done.
 *
 * Note that if you have not enabled tracing extensions via `addTracingExtensions`
 * or you didn't set `tracesSampleRate`, this function will not generate spans
 * and the `span` returned from the callback will be undefined.
 *
 * This function is meant to be used internally and may break at any time. Use at your own risk.
 *
 * @internal
 * @private
 *
 * @deprecated Use `startSpan` instead.
 */
function trace(context, callback) {
  var onError = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : function () {};
  var afterFinish = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : function () {};
  // eslint-disable-next-line deprecation/deprecation
  var hub = getCurrentHub();
  var scope = getCurrentScope();
  // eslint-disable-next-line deprecation/deprecation
  var parentSpan = scope.getSpan();
  var ctx = normalizeContext(context);
  var activeSpan = createChildSpanOrTransaction(hub, parentSpan, ctx);

  // eslint-disable-next-line deprecation/deprecation
  scope.setSpan(activeSpan);
  return handleCallbackErrors(function () {
    return callback(activeSpan);
  }, function (error) {
    activeSpan && activeSpan.setStatus('internal_error');
    onError(error, activeSpan);
  }, function () {
    activeSpan && activeSpan.end();
    // eslint-disable-next-line deprecation/deprecation
    scope.setSpan(parentSpan);
    afterFinish();
  });
}

/**
 * Wraps a function with a transaction/span and finishes the span after the function is done.
 * The created span is the active span and will be used as parent by other spans created inside the function
 * and can be accessed via `Sentry.getSpan()`, as long as the function is executed while the scope is active.
 *
 * If you want to create a span that is not set as active, use {@link startInactiveSpan}.
 *
 * Note that if you have not enabled tracing extensions via `addTracingExtensions`
 * or you didn't set `tracesSampleRate`, this function will not generate spans
 * and the `span` returned from the callback will be undefined.
 */
function startSpan(context, callback) {
  var ctx = normalizeContext(context);
  return runWithAsyncContext(function () {
    return withScope(context.scope, function (scope) {
      // eslint-disable-next-line deprecation/deprecation
      var hub = getCurrentHub();
      // eslint-disable-next-line deprecation/deprecation
      var parentSpan = scope.getSpan();
      var shouldSkipSpan = context.onlyIfParent && !parentSpan;
      var activeSpan = shouldSkipSpan ? undefined : createChildSpanOrTransaction(hub, parentSpan, ctx);

      // eslint-disable-next-line deprecation/deprecation
      scope.setSpan(activeSpan);
      return handleCallbackErrors(function () {
        return callback(activeSpan);
      }, function () {
        // Only update the span status if it hasn't been changed yet
        if (activeSpan) {
          var _spanToJSON = spanToJSON(activeSpan),
            status = _spanToJSON.status;
          if (!status || status === 'ok') {
            activeSpan.setStatus('internal_error');
          }
        }
      }, function () {
        return activeSpan && activeSpan.end();
      });
    });
  });
}

/**
 * @deprecated Use {@link startSpan} instead.
 */
var startActiveSpan = startSpan;

/**
 * Similar to `Sentry.startSpan`. Wraps a function with a transaction/span, but does not finish the span
 * after the function is done automatically. You'll have to call `span.end()` manually.
 *
 * The created span is the active span and will be used as parent by other spans created inside the function
 * and can be accessed via `Sentry.getActiveSpan()`, as long as the function is executed while the scope is active.
 *
 * Note that if you have not enabled tracing extensions via `addTracingExtensions`
 * or you didn't set `tracesSampleRate`, this function will not generate spans
 * and the `span` returned from the callback will be undefined.
 */
function startSpanManual(context, callback) {
  var ctx = normalizeContext(context);
  return runWithAsyncContext(function () {
    return withScope(context.scope, function (scope) {
      // eslint-disable-next-line deprecation/deprecation
      var hub = getCurrentHub();
      // eslint-disable-next-line deprecation/deprecation
      var parentSpan = scope.getSpan();
      var shouldSkipSpan = context.onlyIfParent && !parentSpan;
      var activeSpan = shouldSkipSpan ? undefined : createChildSpanOrTransaction(hub, parentSpan, ctx);

      // eslint-disable-next-line deprecation/deprecation
      scope.setSpan(activeSpan);
      function finishAndSetSpan() {
        activeSpan && activeSpan.end();
      }
      return handleCallbackErrors(function () {
        return callback(activeSpan, finishAndSetSpan);
      }, function () {
        // Only update the span status if it hasn't been changed yet, and the span is not yet finished
        if (activeSpan && activeSpan.isRecording()) {
          var _spanToJSON2 = spanToJSON(activeSpan),
            status = _spanToJSON2.status;
          if (!status || status === 'ok') {
            activeSpan.setStatus('internal_error');
          }
        }
      });
    });
  });
}

/**
 * Creates a span. This span is not set as active, so will not get automatic instrumentation spans
 * as children or be able to be accessed via `Sentry.getSpan()`.
 *
 * If you want to create a span that is set as active, use {@link startSpan}.
 *
 * Note that if you have not enabled tracing extensions via `addTracingExtensions`
 * or you didn't set `tracesSampleRate` or `tracesSampler`, this function will not generate spans
 * and the `span` returned from the callback will be undefined.
 */
function startInactiveSpan(context) {
  if (!hasTracingEnabled()) {
    return undefined;
  }
  var ctx = normalizeContext(context);
  // eslint-disable-next-line deprecation/deprecation
  var hub = getCurrentHub();
  var parentSpan = context.scope ?
  // eslint-disable-next-line deprecation/deprecation
  context.scope.getSpan() : getActiveSpan();
  var shouldSkipSpan = context.onlyIfParent && !parentSpan;
  if (shouldSkipSpan) {
    return undefined;
  }
  var isolationScope = getIsolationScope();
  var scope = getCurrentScope();
  var span;
  if (parentSpan) {
    // eslint-disable-next-line deprecation/deprecation
    span = parentSpan.startChild(ctx);
  } else {
    var _isolationScope$getPr = _objectSpread(_objectSpread({}, isolationScope.getPropagationContext()), scope.getPropagationContext()),
      traceId = _isolationScope$getPr.traceId,
      dsc = _isolationScope$getPr.dsc,
      parentSpanId = _isolationScope$getPr.parentSpanId,
      sampled = _isolationScope$getPr.sampled;

    // eslint-disable-next-line deprecation/deprecation
    span = hub.startTransaction(_objectSpread(_objectSpread({
      traceId: traceId,
      parentSpanId: parentSpanId,
      parentSampled: sampled
    }, ctx), {}, {
      metadata: _objectSpread({
        dynamicSamplingContext: dsc
      }, ctx.metadata)
    }));
  }
  setCapturedScopesOnSpan(span, scope, isolationScope);
  return span;
}

/**
 * Returns the currently active span.
 */
function getActiveSpan() {
  // eslint-disable-next-line deprecation/deprecation
  return getCurrentScope().getSpan();
}
var continueTrace = function continueTrace(_ref, callback) {
  var sentryTrace = _ref.sentryTrace,
    baggage = _ref.baggage;
  // TODO(v8): Change this function so it doesn't do anything besides setting the propagation context on the current scope:
  /*
    return withScope((scope) => {
      const propagationContext = propagationContextFromHeaders(sentryTrace, baggage);
      scope.setPropagationContext(propagationContext);
      return callback();
    })
  */

  var currentScope = getCurrentScope();

  // eslint-disable-next-line deprecation/deprecation
  var _tracingContextFromHe = tracingContextFromHeaders(sentryTrace, baggage),
    traceparentData = _tracingContextFromHe.traceparentData,
    dynamicSamplingContext = _tracingContextFromHe.dynamicSamplingContext,
    propagationContext = _tracingContextFromHe.propagationContext;
  currentScope.setPropagationContext(propagationContext);
  if (DEBUG_BUILD && traceparentData) {
    logger.log("[Tracing] Continuing trace ".concat(traceparentData.traceId, "."));
  }
  var transactionContext = _objectSpread(_objectSpread({}, traceparentData), {}, {
    metadata: dropUndefinedKeys({
      dynamicSamplingContext: dynamicSamplingContext
    })
  });
  if (!callback) {
    return transactionContext;
  }
  return runWithAsyncContext(function () {
    return callback(transactionContext);
  });
};
function createChildSpanOrTransaction(hub, parentSpan, ctx) {
  if (!hasTracingEnabled()) {
    return undefined;
  }
  var isolationScope = getIsolationScope();
  var scope = getCurrentScope();
  var span;
  if (parentSpan) {
    // eslint-disable-next-line deprecation/deprecation
    span = parentSpan.startChild(ctx);
  } else {
    var _isolationScope$getPr2 = _objectSpread(_objectSpread({}, isolationScope.getPropagationContext()), scope.getPropagationContext()),
      traceId = _isolationScope$getPr2.traceId,
      dsc = _isolationScope$getPr2.dsc,
      parentSpanId = _isolationScope$getPr2.parentSpanId,
      sampled = _isolationScope$getPr2.sampled;

    // eslint-disable-next-line deprecation/deprecation
    span = hub.startTransaction(_objectSpread(_objectSpread({
      traceId: traceId,
      parentSpanId: parentSpanId,
      parentSampled: sampled
    }, ctx), {}, {
      metadata: _objectSpread({
        dynamicSamplingContext: dsc
      }, ctx.metadata)
    }));
  }
  setCapturedScopesOnSpan(span, scope, isolationScope);
  return span;
}

/**
 * This converts StartSpanOptions to TransactionContext.
 * For the most part (for now) we accept the same options,
 * but some of them need to be transformed.
 *
 * Eventually the StartSpanOptions will be more aligned with OpenTelemetry.
 */
function normalizeContext(context) {
  if (context.startTime) {
    var ctx = _objectSpread({}, context);
    ctx.startTimestamp = spanTimeInputToSeconds(context.startTime);
    delete ctx.startTime;
    return ctx;
  }
  return context;
}
var SCOPE_ON_START_SPAN_FIELD = '_sentryScope';
var ISOLATION_SCOPE_ON_START_SPAN_FIELD = '_sentryIsolationScope';
function setCapturedScopesOnSpan(span, scope, isolationScope) {
  if (span) {
    addNonEnumerableProperty(span, ISOLATION_SCOPE_ON_START_SPAN_FIELD, isolationScope);
    addNonEnumerableProperty(span, SCOPE_ON_START_SPAN_FIELD, scope);
  }
}

/**
 * Grabs the scope and isolation scope off a span that were active when the span was started.
 */
function getCapturedScopesOnSpan(span) {
  return {
    scope: span[SCOPE_ON_START_SPAN_FIELD],
    isolationScope: span[ISOLATION_SCOPE_ON_START_SPAN_FIELD]
  };
}
export { continueTrace, getActiveSpan, getCapturedScopesOnSpan, startActiveSpan, startInactiveSpan, startSpan, startSpanManual, trace };